import React from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import PublicLayout from '../components/public/PublicLayout';
import { User, FileText, Bell, ArrowLeft, LogOut, ChevronRight } from 'lucide-react';

const cardBase =
  'group relative bg-white rounded-2xl border border-gray-100 p-8 flex flex-col items-center text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:border-[#F5A623]';

const AccountCard = ({ to, icon: Icon, title, subtitle, testid }) => (
  <Link to={to} className={cardBase} data-testid={testid}>
    <div className="w-20 h-20 rounded-2xl bg-[#1A2A4F]/5 group-hover:bg-[#F5A623]/15 transition flex items-center justify-center mb-5">
      <Icon className="w-10 h-10 text-[#1A2A4F] group-hover:text-[#F5A623] transition" />
    </div>
    <h3 className="text-xl font-bold text-[#1A2A4F]">{title}</h3>
    <p className="mt-2 text-sm text-gray-600 max-w-[14rem] leading-relaxed">{subtitle}</p>
    <span className="mt-6 w-full border-t border-gray-100 group-hover:border-[#F5A623] transition" />
    <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#F5A623] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition">
      Open <ChevronRight className="w-3 h-3" />
    </span>
  </Link>
);

const MyAccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // If a child route is rendered (e.g. /my-account/profile), render only that.
  // Otherwise show the 3-card grid.
  const isChild = location.pathname !== '/my-account';

  if (!user) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Please sign in to see your account.</p>
          <Link to="/login" className="mt-4 inline-block bg-[#1A2A4F] text-white font-semibold px-5 py-2.5 rounded-md">Sign In</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="bg-gray-50 min-h-[60vh] py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-start justify-between">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-[#1A2A4F] text-white flex items-center justify-center shadow hover:brightness-95"
              aria-label="Back"
              data-testid="my-account-back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-2 border-2 border-rose-500 text-rose-600 font-bold text-sm px-5 py-2 rounded-md hover:bg-rose-50"
              data-testid="my-account-logout"
            >
              Logout <LogOut className="w-4 h-4" />
            </button>
          </div>

          {!isChild && (
            <>
              <h1 className="mt-6 text-3xl sm:text-4xl font-bold text-[#1A2A4F]" data-testid="my-account-heading">
                My Account
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Welcome back, <span className="font-semibold text-[#1A2A4F]">{user.name || user.email}</span>.
              </p>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="my-account-cards">
                <AccountCard
                  to="/my-account/profile"
                  icon={User}
                  title="Your Profile"
                  subtitle="Tell us about yourself so we can help you find the right program."
                  testid="card-your-profile"
                />
                <AccountCard
                  to="/my-account/applications"
                  icon={FileText}
                  title="Your Applications"
                  subtitle="See and access all your applications in one place."
                  testid="card-your-applications"
                />
                <AccountCard
                  to="/my-account/notifications"
                  icon={Bell}
                  title="Notifications"
                  subtitle="Stay up to date with the latest updates about your applications."
                  testid="card-your-notifications"
                />
              </div>
            </>
          )}

          {isChild && <Outlet />}
        </div>
      </div>
    </PublicLayout>
  );
};

export default MyAccountPage;
